import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpBackend from "i18next-http-backend";

// Translations are loaded over HTTP at runtime from /locales/{lang}/translation.json
// (served as static assets from the public/ directory). This keeps ~2 MB of JSON
// out of the initial JS bundle — only the active language is fetched, and the
// browser caches it across navigations.

// Language metadata for RTL support and formatting
export const LANGUAGE_CONFIG = {
  "en-CA": {
    name: "English (Canada)",
    nativeName: "English",
    dir: "ltr",
    dateFormat: "MM/DD/YYYY",
    currencyCode: "CAD",
    currencySymbol: "$",
    numberFormat: { decimal: ".", thousands: "," }
  },
  "en-US": {
    name: "English (US)",
    nativeName: "English",
    dir: "ltr",
    dateFormat: "MM/DD/YYYY",
    currencyCode: "USD",
    currencySymbol: "$",
    numberFormat: { decimal: ".", thousands: "," }
  },
  "en-GB": {
    name: "English (UK)",
    nativeName: "English",
    dir: "ltr",
    dateFormat: "DD/MM/YYYY",
    currencyCode: "GBP",
    currencySymbol: "£",
    numberFormat: { decimal: ".", thousands: "," }
  },
  "fr-CA": {
    name: "French (Canada)",
    nativeName: "Français",
    dir: "ltr",
    dateFormat: "YYYY-MM-DD",
    currencyCode: "CAD",
    currencySymbol: "$",
    numberFormat: { decimal: ",", thousands: " " }
  },
  "fr-FR": {
    name: "French (France)",
    nativeName: "Français",
    dir: "ltr",
    dateFormat: "DD/MM/YYYY",
    currencyCode: "EUR",
    currencySymbol: "€",
    numberFormat: { decimal: ",", thousands: " " }
  },
  "es-ES": {
    name: "Spanish",
    nativeName: "Español",
    dir: "ltr",
    dateFormat: "DD/MM/YYYY",
    currencyCode: "EUR",
    currencySymbol: "€",
    numberFormat: { decimal: ",", thousands: "." }
  },
  "es-MX": {
    name: "Spanish (Mexico)",
    nativeName: "Español",
    dir: "ltr",
    dateFormat: "DD/MM/YYYY",
    currencyCode: "MXN",
    currencySymbol: "$",
    numberFormat: { decimal: ".", thousands: "," }
  },
  "de-DE": {
    name: "German",
    nativeName: "Deutsch",
    dir: "ltr",
    dateFormat: "DD.MM.YYYY",
    currencyCode: "EUR",
    currencySymbol: "€",
    numberFormat: { decimal: ",", thousands: "." }
  },
  "it-IT": {
    name: "Italian",
    nativeName: "Italiano",
    dir: "ltr",
    dateFormat: "DD/MM/YYYY",
    currencyCode: "EUR",
    currencySymbol: "€",
    numberFormat: { decimal: ",", thousands: "." }
  },
  "pt-BR": {
    name: "Portuguese (Brazil)",
    nativeName: "Português",
    dir: "ltr",
    dateFormat: "DD/MM/YYYY",
    currencyCode: "BRL",
    currencySymbol: "R$",
    numberFormat: { decimal: ",", thousands: "." }
  },
  "ar-SA": {
    name: "Arabic",
    nativeName: "العربية",
    dir: "rtl",
    dateFormat: "DD/MM/YYYY",
    currencyCode: "SAR",
    currencySymbol: "ر.س",
    numberFormat: { decimal: ".", thousands: "," }
  },
  "zh-CN": {
    name: "Chinese (Simplified)",
    nativeName: "中文",
    dir: "ltr",
    dateFormat: "YYYY-MM-DD",
    currencyCode: "CNY",
    currencySymbol: "¥",
    numberFormat: { decimal: ".", thousands: "," }
  },
};

// Supported languages — each is a region-specific code that maps to a base
// language file via the loadPath function below (e.g. en-CA, en-US, en-GB all
// fetch /locales/en/translation.json). The region is preserved at runtime so
// Intl.NumberFormat / DateTimeFormat use the correct regional conventions.
export const SUPPORTED_LANGUAGES = [
  "en-CA",
  "en-US",
  "en-GB",
  "fr-CA",
  "fr-FR",
  "es-MX",
];

// Get all available languages (for future expansion UI)
export const ALL_LANGUAGES = Object.keys(LANGUAGE_CONFIG);

// Helper to get language config
export const getLanguageConfig = (langCode) => {
  return LANGUAGE_CONFIG[langCode] || LANGUAGE_CONFIG["en-CA"];
};

// Helper to check if language is RTL
export const isRTL = (langCode) => {
  const config = getLanguageConfig(langCode);
  return config.dir === "rtl";
};

// Helper to format number based on locale
export const formatNumber = (number, langCode, options = {}) => {
  const config = getLanguageConfig(langCode);
  try {
    return new Intl.NumberFormat(langCode, options).format(number);
  } catch {
    // Fallback formatting
    const { decimal, thousands } = config.numberFormat;
    const [intPart, decPart] = number.toString().split(".");
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return decPart ? `${formattedInt}${decimal}${decPart}` : formattedInt;
  }
};

// Helper to format currency based on locale
export const formatCurrency = (amount, langCode, currencyOverride = null) => {
  const config = getLanguageConfig(langCode);
  const currency = currencyOverride || config.currencyCode;
  try {
    return new Intl.NumberFormat(langCode, {
      style: "currency",
      currency: currency,
    }).format(amount);
  } catch {
    return `${config.currencySymbol}${formatNumber(amount, langCode, { minimumFractionDigits: 2 })}`;
  }
};

// Helper to format date based on locale
export const formatDate = (date, langCode, options = {}) => {
  try {
    return new Intl.DateTimeFormat(langCode, options).format(new Date(date));
  } catch {
    return new Date(date).toLocaleDateString();
  }
};

// Helper to format relative time (e.g., "2 hours ago")
export const formatRelativeTime = (date, langCode) => {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  try {
    const rtf = new Intl.RelativeTimeFormat(langCode, { numeric: "auto" });
    if (diffMins < 1) return rtf.format(0, "minute"); // "just now" or equivalent
    if (diffMins < 60) return rtf.format(-diffMins, "minute");
    if (diffHours < 24) return rtf.format(-diffHours, "hour");
    if (diffDays < 30) return rtf.format(-diffDays, "day");
    return formatDate(date, langCode);
  } catch {
    // Fallback for browsers without RelativeTimeFormat
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  }
};

i18n
  .use(HttpBackend) // Load translations over HTTP from public/locales
  .use(LanguageDetector) // Detect language from browser/localStorage
  .use(initReactI18next) // Pass i18n to react-i18next
  .init({
    fallbackLng: "en-CA", // Fallback language
    supportedLngs: SUPPORTED_LANGUAGES,

    backend: {
      // Map any region-specific code to a single base-language file:
      //   en-CA, en-US, en-GB → /locales/en/translation.json
      //   fr-CA, fr-FR         → /locales/fr/translation.json
      //   es-MX                → /locales/es/translation.json
      loadPath: (lngs) => {
        const lng = Array.isArray(lngs) ? lngs[0] : lngs;
        const base = String(lng).split("-")[0];
        return `/locales/${base}/translation.json`;
      },
    },

    detection: {
      // Order of language detection — querystring first so ?lang=fr-CA URLs
      // render in the correct language (critical for SEO / Googlebot)
      order: ["querystring", "localStorage", "navigator", "htmlTag"],
      // Cache language in localStorage
      caches: ["localStorage"],
      lookupLocalStorage: "i18nextLng",
      lookupQuerystring: "lang",
    },

    interpolation: {
      escapeValue: false, // React already handles XSS
      // Custom format function for numbers, dates, currencies
      format: (value, format, lng) => {
        if (format === "number") return formatNumber(value, lng);
        if (format === "currency") return formatCurrency(value, lng);
        if (format === "date") return formatDate(value, lng);
        if (format === "relativeTime") return formatRelativeTime(value, lng);
        return value;
      },
    },

    react: {
      useSuspense: true, // Enable suspense for loading
    },

    // Enable pluralization
    pluralSeparator: "_",
    contextSeparator: "_",
  });

// Update document direction when language changes
i18n.on("languageChanged", (lng) => {
  const config = getLanguageConfig(lng);
  document.documentElement.dir = config.dir;
  document.documentElement.lang = lng;

  // Add/remove RTL class for CSS styling
  if (config.dir === "rtl") {
    document.documentElement.classList.add("rtl");
  } else {
    document.documentElement.classList.remove("rtl");
  }
});

// Set initial direction
const initialConfig = getLanguageConfig(i18n.language);
document.documentElement.dir = initialConfig.dir;
document.documentElement.lang = i18n.language;

export default i18n;
